<template>
  <div class="col-md-12">
    <div v-if="tokenLoading" class="register-loading">
      <div class="spinner-border spinner-border-lg" role="status">
        <span class="sr-only">Validation en cours...</span>
      </div>
      <p class="register-loading-text">Validation de votre lien en cours...</p>
    </div>

    <div v-else-if="tokenError" class="register-token-state">
      <div class="error-state-box">
        <div class="error-state-icon">⚠️</div>
        <div class="error-state-content">
          <p class="error-state-title">Erreur de validation</p>
          <p class="error-state-text">{{ tokenError }}</p>
        </div>
      </div>
      <router-link :to="{ name: 'public-register' }" class="btn btn-primary btn-block">
        Recommencer l'inscription
      </router-link>
    </div>

    <Form v-else ref="registerForm" @submit="handleRegister" @invalid-submit="handleInvalidSubmit" :validation-schema="schema" v-slot="{ values }">
      <div v-if="!successful">
        <Header></Header>
        <div class="register-obligatoire">
          * champs obligatoires
        </div>
        <div class="header-group">
          <h1><img alt="" src="@/assets/img/underline.png"> MON PROFIL</h1>
        </div>

        <div id="civility-anchor" class="form-group">
          <Field as="select" autocomplete="honorific-prefix" class="form-control" name="civility">
            <option disabled value="">Civilité*</option>
            <option value="Monsieur">Monsieur</option>
            <option value="Madame">Madame</option>
          </Field>
          <ErrorMessage class="error-feedback" name="civility"/>
        </div>

        <div id="lastname-anchor" class="form-group">
          <Field autocomplete="family-name" class="form-control" name="lastname" placeholder="Nom de naissance*" type="text"/>
          <ErrorMessage class="error-feedback" name="lastname"/>
        </div>

        <div v-if="values.civility === 'Madame'" id="marriedname-anchor" class="form-group">
          <Field
              autocomplete="off"
              class="form-control"
              name="marriedname"
              placeholder="Nom marital"
              type="text"
          />
          <ErrorMessage class="error-feedback" name="marriedname"/>
        </div>

        <div id="firstname-anchor" class="form-group">
          <Field autocomplete="given-name" class="form-control" name="firstname" placeholder="Prénom*" type="text"/>
          <ErrorMessage class="error-feedback" name="firstname"/>
        </div>

        <div id="birthday-anchor" class="form-group">
          <Field
              autocomplete="bday"
              class="form-control"
              name="birthday"
              onblur="if(this.value === ''){(this.type='text')}"
              onfocus="(this.type='date')"
              onmouseenter="(this.type='date')"
              placeholder="Date de naissance*"
              type="text"
          />
          <ErrorMessage class="error-feedback" name="birthday"/>
        </div>

        <div id="nationality-anchor" class="form-group">
          <Field as="select" autocomplete="country-name" class="form-control" name="nationality">
            <option disabled value="">Nationalité*</option>
            <option v-for="nationality in nationalities" :key="nationality.id" :value="nationality.id">
              {{ nationality.label }}
            </option>
          </Field>
          <ErrorMessage class="error-feedback" name="nationality"/>
        </div>

        <div id="born-in-france-anchor" class="form-group custom-control custom-checkbox">
          <input id="bornAbroad" v-model="bornAbroad" class="custom-control-input checkbox-lg" type="checkbox">
          <label class="custom-control-label" for="bornAbroad">Né(e) à l'étranger</label>
        </div>

        <Field id="birth_department" class="form-control" hidden name="birth_department" type="text"/>

        <div v-if="!bornAbroad">
          <div id="birthcity-anchor" class="form-group">
            <vue3-simple-typeahead
                id="birthCityList"
                ref="birthCityList"
                v-model="birthCitySearch"
                :item-projection="birthCityItemProjection"
                :items="birthCityList"
                :min-input-length="3"
                name="birthCityList"
                placeholder="Ville de naissance*"
                @on-blur="onBirthCityBlur"
                @on-focus="onBirthCityFocus"
                @on-input="onBirthCityInput"
                @select-item="selectBirthCity"
            >
              <template #list-item-text="{ item }">
                {{ item.name }} ({{ item.postalCode }})
              </template>
              <template #list-footer>
                <div v-if="showBirthCitySelectionHint && birthCityList.length">
                  ⚠️ Voulez-vous dire : <strong>{{ birthCityList[0].name }} ({{ birthCityList[0].postalCode }})</strong> ? Cliquez sur la proposition.
                </div>
              </template>
            </vue3-simple-typeahead>
          </div>
        </div>

        <Field id="birthCityFieldId" class="form-control" hidden name="birthCityFieldId" type="text"/>

        <div id="birthplace-anchor" :style="{ display: bornAbroad ? 'block' : 'none' }" class="form-group">
          <input id="birthplace" name="birthplace" v-model="birthplace" autocomplete="off" class="form-control" placeholder="Lieu de naissance*" type="text"/>
        </div>

        <div class="header-group">
          <h1><img alt="" src="@/assets/img/underline.png"> PARAMÈTRES DE COMPTE</h1>
        </div>

        <div id="email-anchor" class="form-group">
          <input :value="verifiedEmail" class="form-control" type="email" disabled>
        </div>

        <PasswordField
            :scroll-on-validation="scrollAnchor"
            :show-toggle="true"
            anchor-id="password-anchor"
            autocomplete="new-password"
            name="password"
            placeholder="Mot de passe*"
        />
        <PasswordField
            :is-confirmation="true"
            :scroll-on-validation="scrollAnchor"
            :show-toggle="true"
            anchor-id="password2-anchor"
            autocomplete="new-password"
            name="password2"
            password-field-name="password"
            placeholder="Confirmer le mot de passe*"
        />

        <div class="header-group">
          <h1><img alt="" src="@/assets/img/underline.png"> COORDONNÉES</h1>
        </div>

        <div id="address-anchor" class="form-group">
          <Field autocomplete="street-address" class="form-control" name="address"  placeholder="Adresse*" type="text"/>
          <ErrorMessage class="error-feedback" name="address"/>
        </div>

        <div class="form-group">
          <vue3-simple-typeahead
              id="cityList"
              ref="cityList"
              v-model="search"
              :item-projection="cityItemProjection"
              :items="list"
              :min-input-length="3"
              name="cityList"
              placeholder="Ville*"
              @blur="onBlur"
              @select-item="selectItem"
          >
            <template #list-item-text="{ item }">
              {{ item.name }} ({{ item.postalCode }})
            </template>
            <template #list-footer>
              <div v-if="showCitySelectionHint && list.length">
                ⚠️ Voulez-vous dire : <strong>{{ list[0].name }} ({{ list[0].postalCode }})</strong> ? Cliquez sur la proposition.
              </div>
            </template>
          </vue3-simple-typeahead>
        </div>

        <Field id="cityField" class="form-control" hidden name="city_hidden" type="text"/>
        <ErrorMessage class="error-feedback" name="city_hidden"/>

        <div id="phone_number-anchor" class="form-group">
          <Field autocomplete="tel" class="form-control" name="phone_number" placeholder="Téléphone*" type="text"/>
          <ErrorMessage class="error-feedback" name="phone_number"/>
        </div>

        <div class="custom-control custom-checkbox">
          <input id="checkCondition" v-model="checkCondition" class="custom-control-input checkbox-lg" type="checkbox">
          <label class="custom-control-label" for="checkCondition">Pour créer votre compte, veuillez accepter nos termes d'utilisation et notre politique de confidentialité</label>
        </div>

        <div class="register-rgpd">
          Les données personnelles recueillies dans ce formulaire font l'objet d'un traitement automatisé conforme au règlement européen 2016/679 sur la protection des données (RGPD) et la loi Informatique, fichiers et libertés (LIL) n° 78-17 du 6 janvier 1978 modifiée par la loi n° 2004-801 du 6 août 2004 et la loi n°2018-493 du 20 juin 2018. Les informations collectées sont destinées aux services internes de l’organisme de formation public APFORM. Pour plus d’information à ce sujet, veuillez consulter notre politique de confidentialité. Pour l’exercice de vos droits, veuillez nous contacter via : postmaster@apform.fr
        </div>

        <div class="form-group">
          <button :disabled="isDisabled" class="btn btn-primary btn-block">
            <span v-show="loading" class="spinner-border spinner-border-sm"></span>
            Créer un compte
          </button>
        </div>
      </div>
    </Form>
  </div>
</template>

<script>
import {ErrorMessage, Field, Form} from "vee-validate";
import * as yup from "yup";
import Header from "./Header.vue";
import PasswordField from "@/components/PasswordField.vue";
import Swal from "sweetalert2";
import CityApi from "@/api/CityApi";
import NationalityApi from "@/api/NationalityApi";
import AuthApi from "@/api/AuthApi";
import RouteEnum from "@/router/RouteEnum";
import {createDebouncedSearch} from "@/utils/debouncedSearch";

export default {
  name: "Register",
  components: {Form, Field, ErrorMessage, Header, PasswordField},
  data() {
    return {
      successful: false,
      loading: false,
      tokenLoading: true,
      tokenError: "",
      verifiedEmail: "",
      signupToken: "",
      message: "",
      search: "",
      checkCondition: false,
      bornAbroad: false,
      birthCitySearch: "",
      birthCityList: [],
      birthCityInputFocused: false,
      showBirthCitySelectionHint: false,
      birthplace: "",
      list: [],
      selectedCityItem: null,
      showCitySelectionHint: false,
      nationalities: [],
      // SCHEMA DE VALIDATION GLOBAL (Anti-freeze)
      schema: yup.object({
        civility: yup.string().required("La civilité est nécessaire"),
        lastname: yup.string()
            .required("Le nom est nécessaire")
            .matches(/^[A-Za-zÀ-ÿ0-9 ’'-.]*$/, "Le nom n'est pas valide")
            .min(2, "Doit comporter au moins 2 caractères"),
        marriedname: yup.string().nullable()
            .matches(/^[A-Za-zÀ-ÿ0-9 ’'-.]*$/, "Le nom marital n'est pas valide")
            .test("not-same", "Le nom marital doit être différent du nom de naissance", function(value) {
              const { lastname } = this.parent;
              if (!value || !lastname) return true;
              return value.trim().toLowerCase() !== lastname.trim().toLowerCase();
            }),
        firstname: yup.string()
            .required("Le prénom est nécessaire")
            .matches(/^[A-Za-zÀ-ÿ0-9 ’'-.]*$/, "Le prénom n'est pas valide")
            .min(2, "Doit comporter au moins 2 caractères"),
        birthday: yup.date().nullable()
            .required("La date de naissance est nécessaire")
            .typeError("La date de naissance est invalide")
            .test("valid-date", "La date de naissance est invalide", (val) => {
              return val instanceof Date && !isNaN(val);
            })
            .max(new Date(), "La date de naissance ne peut pas être dans le futur")
            .test("age-min", "Vous devez avoir au moins 18 ans", (val) => {
              if (!val) return false;
              const today = new Date();
              const birthDate = new Date(val);

              let age = today.getFullYear() - birthDate.getFullYear();
              if (
                  today.getMonth() < birthDate.getMonth() ||
                  (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate())
              ) {
                age--;
              }

              return age >= 18;
            })
            .test("age-max", "La date de naissance semble invalide", (val) => {
              if (!val) return false;
              const today = new Date();
              const birthDate = new Date(val);

              let age = today.getFullYear() - birthDate.getFullYear();
              return age <= 70;
            }),
        nationality: yup.string().required("La nationalité est nécessaire"),
        address: yup.string().required("L'adresse est nécessaire").min(6, "Doit comporter au moins 6 caractères").max(80, "Doit comporter au maximum 80 caractères").matches(/^[A-Za-zÀ-ÿ0-9 ’'-.]*$/, "L'adresse n'est pas valide"),
        city_hidden: yup.string().required("Veuillez sélectionner une ville dans la liste"),
        password: yup.string()
            .required("Le mot de passe est nécessaire")
            .min(6, "Mot de passe invalide : minimum 6 caractères")
            .max(40, "Doit comporter au maximum 40 caractères")
            .matches(/^[a-zA-Z0-9$!#?\-_]+$/, "Mot de passe invalide : seuls les lettres (sans accent), chiffres et caractères spéciaux ($ ! # ? - _) sont autorisés")
            .matches(/^(?=.*[a-zA-Z])(?=.*\d)(?=.*[$!#?\-_]).*$/, "Mot de passe invalide : au moins une lettre (sans accent), un chiffre et un caractère spécial ($ ! # ? - _) sont requis"),
        password2: yup.string()
            .required("Le mot de passe est nécessaire")
            .oneOf([yup.ref('password')], 'Mot de passe invalide : différent du précédent'),
        phone_number: yup.string()
            .required("Le numéro de téléphone est nécessaire")
            .matches(/^((\+)33|0|0033)[1-9](\d{2}){4}$/, "Le numéro de téléphone n'est pas valide"),
      }),
    };
  },
  created() {
    this._citySearch = createDebouncedSearch((term) => CityApi.getCities(term), {
      onResults: (results) => this.list = results,
      onError: (error) => console.error('Error loading cities:', error)
    });
    this._birthCitySearch = createDebouncedSearch((term) => CityApi.getCities(term), {
      onResults: (results) => {
        this.birthCityList = results;
        this.showBirthCityHintIfNeeded();
      },
      onError: (error) => console.error('Error loading birth cities:', error)
    });
  },
  computed: {
    isDisabled() {
      return !this.checkCondition || this.loading || this.tokenLoading || !this.verifiedEmail;
    }
  },
  async mounted() {
    const token = this.$route.query.token;
    if (!token) {
      this.tokenError = "Lien de validation manquant.";
      this.tokenLoading = false;
      return;
    }

    this.signupToken = token;
    try {
      const response = await AuthApi.verifySignUpEmailToken(token);
      this.verifiedEmail = response.data.email;
    } catch (error) {
      this.tokenError = error.response?.data?.message || "Le lien de validation est invalide.";
      this.tokenLoading = false;
      return;
    }

    this.tokenLoading = false;
    // Initialisation autocomplete off
    ['cityList', 'birthCityList'].forEach(id => {
      const el = document.getElementById(id);
      if (el) {
        el.autocomplete = 'off';
        el.autocapitalize = 'off';
        el.spellcheck = false;
      }
    });
    try {
      this.nationalities = await NationalityApi.getNationalities();
    } catch (error) {
      console.error('Erreur nationalités:', error);
    }
  },
  watch: {
    async bornAbroad(newVal) {
      if (newVal) {
        this.birthCityInputFocused = false;
        this.showBirthCitySelectionHint = false;
        document.getElementById('birth_department').value = '99';
        document.getElementById('birthCityFieldId').value = '';
        this.birthCitySearch = "";
        this._birthCitySearch.cancel();
        this.birthCityList = [];
        this.birthplace = "";
        if (this.$refs.birthCityList) this.$refs.birthCityList.clearInput();
        await this.$nextTick();
      }
    },
    search(newVal) {
      this.showCitySelectionHint = false;
      if (this.selectedCityItem) {
        const label = this.selectedCityItem.name + ' (' + this.selectedCityItem.postalCode + ')';
        if (newVal !== label) {
          // L'utilisateur a modifié le texte → on invalide
          this.selectedCityItem = null;
          document.getElementById('cityField').value = '';
          this.$refs.registerForm.setFieldValue('city_hidden', '');
        } else {
          return; // texte identique à la sélection, rien à faire
        }
      }
      // Pas de sélection active → recherche différée après la saisie
      this._citySearch(newVal);
    }
  },
  methods: {
    cityItemProjection(item) {
      const label = item.name + ' (' + item.postalCode + ')';
      const input = this.$refs.cityList?.input || '';

      return label.toLowerCase().includes(input.toLowerCase())
          ? label
          : label.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    },
    selectItem(item) {
      this._citySearch.cancel();
      this.list = [];
      this.selectedCityItem = item;
      this.showCitySelectionHint = false;
      this.search = item.name + ' (' + item.postalCode + ')';
      this.$refs.cityList.input = this.search;
      this.$refs.cityList.isInputFocused = false;
      document.getElementById('cityField').value = item.id;
      this.$refs.registerForm.setFieldValue('city_hidden', item.id);
    },
    onBlur() {
      this._citySearch.cancel();
      if (!this.selectedCityItem) {
        this.$refs.registerForm.setFieldValue('city_hidden', '');
        if (this.list.length) {
          this.showCitySelectionHint = true;
          this.$nextTick(() => {
            this.$refs.cityList.isInputFocused = true;
          });
        }
      }
    },
    handleInvalidSubmit({errors}) {
      if (errors.city_hidden && this.list.length) {
        this.showCitySelectionHint = true;
        this.$nextTick(() => {
          this.$refs.cityList.isInputFocused = true;
        });
      }
    },
    birthCityItemProjection(item) {
      const label = item.name + ' (' + item.postalCode + ')';
      const input = this.$refs.birthCityList?.input || '';

      return label.toLowerCase().includes(input.toLowerCase())
          ? label
          : label.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    },
    selectBirthCity(item) {
      this._birthCitySearch.cancel();
      this.birthCityList = [];
      this.birthCityInputFocused = false;
      this.showBirthCitySelectionHint = false;
      document.getElementById('birthCityFieldId').value = item.id;
      const pc = item.postalCode?.toString() || '';
      // DOM/TOM : codes 97xxx et 98xxx utilisent 3 chiffres pour le département
      const dept = (pc.startsWith('97') || pc.startsWith('98')) ? pc.slice(0, 3) : pc.slice(0, 2);
      document.getElementById('birth_department').value = dept;
      this.birthplace = item.name;
      this.$refs.birthCityList.input = item.name + ' (' + item.postalCode + ')';
      this.$refs.birthCityList.isInputFocused = false;
    },
    onBirthCityFocus() {
      this.birthCityInputFocused = true;
      this.showBirthCitySelectionHint = false;
    },
    onBirthCityInput({input}) {
      this.birthCitySearch = input;
      this.showBirthCitySelectionHint = false;
      document.getElementById('birthCityFieldId').value = '';
      document.getElementById('birth_department').value = '';
      this.birthplace = '';
      this._birthCitySearch(input);
    },
    onBirthCityBlur() {
      this.birthCityInputFocused = false;
      this.showBirthCityHintIfNeeded();
    },
    showBirthCityHintIfNeeded() {
      const birthCityId = document.getElementById('birthCityFieldId')?.value;
      if (!this.bornAbroad && !this.birthCityInputFocused && !birthCityId && this.birthCityList.length) {
        this.showBirthCitySelectionHint = true;
        this.$nextTick(() => {
          if (this.showBirthCitySelectionHint && this.$refs.birthCityList) {
            this.$refs.birthCityList.isInputFocused = true;
          }
        });
      }
    },
    handleRegister(user) {
      user.birth_department = document.getElementById('birth_department').value;
      user.birth_city_id = document.getElementById('birthCityFieldId').value;
      user.city = document.getElementById('cityField').value;
      user.birthplace = this.birthplace;
      user.signupToken = this.signupToken;
      this.register(user);
    },
    async register(user) {
      this.loading = true;
      try {
        await this.$store.dispatch("auth/register", user);
        this.successful = true;
        this.$router.push({
          name: RouteEnum.REGISTER_SUCCESS,
          query: {
            email: this.verifiedEmail,
            step: "account-created",
          },
        });
      } catch (error) {
        console.log(error);
        Swal.fire({
          icon: 'error',
          text: error.response?.data?.message || error || "Une erreur est survenue"
        });
      } finally {
        this.loading = false;
      }
    },
    scrollAnchor(anchorId) {
      const el = document.getElementById(anchorId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  },
  beforeUnmount() {
    this._citySearch?.cancel();
    this._birthCitySearch?.cancel();
  }
};
</script>

<style scoped>
.register-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px 0;
}

.register-loading-text {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

.register-token-state {
  max-width: 600px;
  margin: 40px auto;
  padding: 40px 30px;
  text-align: center;
}

.error-state-box {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 20px;
  background: #fef2f2;
  border-radius: 8px;
  border: 1px solid #fecaca;
  text-align: left;
  margin-bottom: 24px;
}

.error-state-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.error-state-content {
  flex: 1;
}

.error-state-title {
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
  font-size: 15px;
}

.error-state-text {
  color: #6b7280;
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
}
</style>
